import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { API_URL } from '../../utils/config';

const getCategoryIdFromUrl = (pathname) => {
  const categoryMatch = pathname.match(/\/category\/([^/]+)/);
  if (categoryMatch) {
    return { categorySlug: categoryMatch[1] };
  }

  const attributeMatch = pathname.match(/\/product-category\/(\d+)/);
  if (attributeMatch) {
    return { categoryId: attributeMatch[1] };
  }

  return {};
};

const sortFilters = (data) => {
  return [...data].sort((a, b) => {
    const aIsColor = a.title.toLowerCase().includes('color') ||
                    a.title.toLowerCase().includes('colour');
    const bIsColor = b.title.toLowerCase().includes('color') ||
                    b.title.toLowerCase().includes('colour');

    if (aIsColor && !bIsColor) return -1;
    if (!aIsColor && bIsColor) return 1;
    return 0;
  });
};

export const parseFiltersFromUrl = (search) => {
  const searchParams = new URLSearchParams(search);
  const currentFilters = {};

  for (const [key, value] of searchParams.entries()) {
    if (key.startsWith('filter_')) {
      const attributeId = key.replace('filter_', '');
      currentFilters[attributeId] = value.split(',').map((id) => parseInt(id, 10));
    }
  }

  return currentFilters;
};

export const buildFilterUrl = (pathname, search, filters, { resetPage = true } = {}) => {
  const searchParams = new URLSearchParams(search);

  for (const key of [...searchParams.keys()]) {
    if (key.startsWith('filter_')) {
      searchParams.delete(key);
    }
  }

  Object.entries(filters).forEach(([parentId, childIds]) => {
    if (childIds.length > 0) {
      searchParams.set(`filter_${parentId}`, childIds.join(','));
    }
  });

  if (resetPage) {
    searchParams.set('page', '1');
  }

  return `${pathname}?${searchParams.toString()}`;
};

export const clearAllFilterParams = (pathname, search) => {
  const searchParams = new URLSearchParams(search);

  for (const key of [...searchParams.keys()]) {
    if (key.startsWith('filter_')) {
      searchParams.delete(key);
    }
  }

  return `${pathname}?${searchParams.toString()}`;
};

export const resolveFilterChips = (selectedFilters, filters) => {
  if (!filters.length) return [];

  const chips = [];

  Object.entries(selectedFilters).forEach(([attributeId, childIds]) => {
    const filterGroup = filters.find((f) => String(f.id) === String(attributeId));
    if (!filterGroup) return;

    childIds.forEach((childId) => {
      const option = filterGroup.children.find((c) => c.attribute_id === childId);
      if (option) {
        chips.push({
          attributeId,
          childId,
          label: option.displayTitle,
        });
      }
    });
  });

  return chips;
};

const useFilterOptions = () => {
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        setLoading(true);
        setError(null);

        const categoryParams = getCategoryIdFromUrl(location.pathname);

        let url = `${API_URL}/attributes/filters`;

        if (Object.keys(categoryParams).length > 0) {
          const searchParams = new URLSearchParams();

          if (categoryParams.categoryId) {
            searchParams.append('categoryId', categoryParams.categoryId);
          }

          if (categoryParams.categorySlug) {
            searchParams.append('categorySlug', categoryParams.categorySlug);
          }

          url += `?${searchParams.toString()}`;
        }

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Failed to fetch filters');
        }

        const data = await response.json();
        setFilters(sortFilters(data));
      } catch (fetchError) {
        console.error('Error fetching filters:', fetchError);
        setError(fetchError.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFilters();
  }, [location.pathname]);

  return { filters, loading, error };
};

export default useFilterOptions;
